import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQC } from './QCContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Clock,
  MapPin,
  Calendar,
  Users,
  Star,
  Briefcase,
  Heart,
} from 'lucide-react';

export const QCBrowseJobs = () => {
  const navigate = useNavigate();
  const { api, user } = useQC();
  const [jobs, setJobs] = useState([]);
  const [interests, setInterests] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/canvass/jobs');
      const availableJobs = (response.data || []).filter(
        j => j.status === 'open' || j.status === 'in_progress'
      );
      setJobs(availableJobs);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleExpressInterest = async (jobId) => {
    try {
      await api.post(`/canvass/jobs/${jobId}/interest`);
      toast.success('Interest expressed! You will be prioritized for this job.');
      setInterests(prev => ({ ...prev, [jobId]: true }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to express interest');
    }
  };

  const handleRemoveInterest = async (jobId) => {
    try {
      await api.delete(`/canvass/jobs/${jobId}/interest`);
      toast.success('Interest removed');
      setInterests(prev => ({ ...prev, [jobId]: false }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove interest');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="qc-browse-jobs">
      <Button variant="ghost" onClick={() => navigate('/quickcanvass')} className="gap-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Dashboard
      </Button>

      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight">
          Browse Available Jobs
        </h1>
        <p className="text-muted-foreground mt-2">
          Express interest to be prioritized when canvassing begins
        </p>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Heart className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h3 className="font-bold text-blue-900 dark:text-blue-100">How Job Selection Works</h3>
              <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                Click "I'm Interested" on jobs you want. When canvassing starts, employees who expressed interest 
                are contacted first (still in ranking order), then others. You'll receive an offer if selected.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Jobs Grid */}
      {jobs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No jobs available at the moment</p>
            <p className="text-sm text-muted-foreground mt-2">Check back later for new overtime opportunities</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {jobs.map((job) => (
            <Card 
              key={job.id} 
              className={`hover:border-primary transition-colors ${
                interests[job.id] ? 'border-2 border-green-500 dark:border-green-600' : ''
              }`}
              data-testid={`job-card-${job.id}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-xl">{job.title}</CardTitle>
                      {interests[job.id] && (
                        <Badge variant="default" className="bg-green-600">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          Interested
                        </Badge>
                      )}
                    </div>
                    <CardDescription className="space-y-2">
                      {job.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {job.location}
                        </div>
                      )}
                      {job.start_date && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {job.start_date}
                        </div>
                      )}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {job.crew_status ? 
                        Object.values(job.crew_status).reduce((a, b) => a + b.filled, 0) + '/' +
                        Object.values(job.crew_status).reduce((a, b) => a + b.required, 0) + ' filled'
                        : 'Crew TBD'
                      }
                    </span>
                  </div>
                  
                  {interests[job.id] ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleRemoveInterest(job.id)}
                      className="gap-1"
                    >
                      <Heart className="h-4 w-4 fill-current text-red-500" />
                      Remove Interest
                    </Button>
                  ) : (
                    <Button 
                      size="sm"
                      onClick={() => handleExpressInterest(job.id)}
                      className="gap-1"
                    >
                      <Heart className="h-4 w-4" />
                      I'm Interested
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default QCBrowseJobs;
